import { useCallback } from 'react';
import { getSelectedAccountInfo } from '@ibk/shared-auth';
import { useTranslation } from '@ibk/shared-translation';
import { AccountTypeAcronym, DocumentTypeNumber, FavoredContactDetailsType } from '../../enums';
import { usePostCreateFavoredContactMutation } from '../../services/pixFavoredContactApi';
function mapPixTypeToPixTypeEnum(pixType) {
    return pixType;
}
function getDocumentTypeFromTaxIdentifier(taxIdentifier) {
    const cleanTaxId = taxIdentifier.replace(/\D/g, '');
    return cleanTaxId.length === 11 ? DocumentTypeNumber.CPF : DocumentTypeNumber.CNPJ;
}
export function useAddFavoredContact() {
    const { t } = useTranslation();
    const [createFavoredContact, { isLoading, error }] = usePostCreateFavoredContactMutation();
    const addFavoredContact = useCallback(async (data) => {
        if (!data.pixKeyDetails?.name || !data.pixKeyDetails?.taxIdentifier) {
            throw new Error(t('incomplete-contact-data'));
        }
        const pixKeyType = mapPixTypeToPixTypeEnum(data.keyType);
        const accountInfo = getSelectedAccountInfo();
        if (!accountInfo?.accountId) {
            throw new Error(t('account-info-not-available'));
        }
        const payload = {
            accountId: accountInfo.accountId,
            name: data.pixKeyDetails.name,
            details: {
                type: FavoredContactDetailsType.PIX_KEY,
                data: {
                    pixKey: data.keyValue,
                    pixKeyType: pixKeyType,
                },
                description: `Chave PIX - ${data.pixKeyDetails.name}`,
            },
        };
        await createFavoredContact(payload).unwrap();
    }, [createFavoredContact, t]);
    const addFavoredBankAccountContact = useCallback(async (pixAgencyAccount) => {
        const documentNumber = pixAgencyAccount.document?.replace(/[^0-9A-Za-z]/g, '').toUpperCase();
        const accountNumber = pixAgencyAccount.bankAccount?.replace(/\D/g, '');
        if (!pixAgencyAccount.name ||
            !documentNumber ||
            !accountNumber ||
            !pixAgencyAccount.bankBranch ||
            !pixAgencyAccount.bankCode) {
            throw new Error(t('incomplete-contact-data'));
        }
        const accountInfo = getSelectedAccountInfo();
        if (!accountInfo?.accountId) {
            throw new Error(t('account-info-not-available'));
        }
        const accountTypeDescription = pixAgencyAccount.accountType === AccountTypeAcronym.SAVING_ACCOUNT ? 'poupança' : 'corrente';
        const payload = {
            accountId: accountInfo.accountId,
            name: pixAgencyAccount.name,
            documentType: documentNumber.length === 11 ? DocumentTypeNumber.CPF : DocumentTypeNumber.CNPJ,
            documentNumber,
            details: {
                type: FavoredContactDetailsType.BANK_ACCOUNT,
                data: {
                    bankCode: pixAgencyAccount.bankCode,
                    branchNumber: pixAgencyAccount.bankBranch,
                    accountNumber,
                    accountType: pixAgencyAccount.accountType,
                },
                description: `Conta ${accountTypeDescription} - ${pixAgencyAccount.name}`,
            },
        };
        await createFavoredContact(payload).unwrap();
    }, [createFavoredContact, t]);
    return {
        addFavoredContact,
        addFavoredBankAccountContact,
        loading: isLoading,
        error: error ? t('error-adding-contact-to-favorites') : undefined,
    };
}
